// Polyfills for older browsers (Huawei compatibility)
import 'core-js/stable'
import 'regenerator-runtime/runtime'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import { useAuthStore } from './stores/auth'
import { useThemeStore } from './stores/theme'
import { logBrowserCompatibility, isHuaweiDevice, isOldAndroid } from './utils/browserCompatibility'
import { initializeClarityTracking } from './utils/clarity'
import { initializeTheme } from './composables/useTheme'
import { iframeCommunication } from './utils/iframeCommunication'
import { syncSystemColorSchemeListener } from './utils/systemColorSchemeWatcher'
import { setupCacheInvalidation } from './utils/cacheManager'
import { setupI18n } from './i18n'

/** `mode` query from `location.search` or from `location.hash` (some iframe embeds). */
function readModeQueryFromWindow(): string | null {
  try {
    const fromSearch = new URLSearchParams(window.location.search).get('mode')
    if (fromSearch) return fromSearch.trim() || null
    const hash = window.location.hash
    const q = hash.indexOf('?')
    if (q !== -1) {
      const fromHash = new URLSearchParams(hash.slice(q + 1)).get('mode')
      if (fromHash) return fromHash.trim() || null
    }
  } catch {
    // ignore
  }
  return null
}

type SupportedFallbackLocale = 'en' | 'es' | 'fr' | 'pt'

const fallbackMessages: Record<SupportedFallbackLocale, {
  applicationError: string
  incompatibleDevice: string
  tryDifferentBrowser: string
  browserCompatibilityError: string
  missingFeatures: string
  unknownError: string
}> = {
  en: {
    applicationError: 'Application Error',
    incompatibleDevice: 'Sorry, this application is not compatible with your device.',
    tryDifferentBrowser: 'Please try using a different browser or update your device.',
    browserCompatibilityError: 'Browser Compatibility Error',
    missingFeatures: 'Your browser is missing required features',
    unknownError: 'Unknown error'
  },
  es: {
    applicationError: 'Error de Aplicación',
    incompatibleDevice: 'Lo sentimos, esta aplicación no es compatible con su dispositivo.',
    tryDifferentBrowser: 'Pruebe con otro navegador o actualice su dispositivo.',
    browserCompatibilityError: 'Error de Compatibilidad del Navegador',
    missingFeatures: 'A su navegador le faltan funciones requeridas',
    unknownError: 'Error desconocido'
  },
  fr: {
    applicationError: "Erreur d'Application",
    incompatibleDevice: "Désolé, cette application n'est pas compatible avec votre appareil.",
    tryDifferentBrowser: 'Veuillez essayer un autre navigateur ou mettre à jour votre appareil.',
    browserCompatibilityError: 'Erreur de Compatibilité du Navigateur',
    missingFeatures: 'Il manque des fonctionnalités requises à votre navigateur',
    unknownError: 'Erreur inconnue'
  },
  pt: {
    applicationError: 'Erro da Aplicação',
    incompatibleDevice: 'Desculpe, esta aplicação não é compatível com o seu dispositivo.',
    tryDifferentBrowser: 'Tente usar outro navegador ou atualize o seu dispositivo.',
    browserCompatibilityError: 'Erro de Compatibilidade do Navegador',
    missingFeatures: 'O seu navegador não tem recursos obrigatórios',
    unknownError: 'Erro desconhecido'
  }
}

function readFallbackLocale(): SupportedFallbackLocale {
  try {
    const locale = new URLSearchParams(window.location.search).get('locale')?.toLowerCase()
    if (locale === 'es' || locale === 'fr' || locale === 'pt') return locale
  } catch {
    // ignore
  }
  return 'en'
}

function fallbackText(key: keyof typeof fallbackMessages.en): string {
  return fallbackMessages[readFallbackLocale()][key]
}

// Initialize Microsoft Clarity
(function(c: any, l: any, a: any, r: any, i: any) {
  c[a] = c[a] || function(...args: any[]) { (c[a].q = c[a].q || []).push(args) };
  const t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
  const y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
})(window, document, "clarity", "script", "tnf8vtdq3i");

// Initialize Clarity tracking after a short delay to ensure Clarity is loaded
setTimeout(() => {
  initializeClarityTracking();
}, 2000);

// Initialize Plausible Analytics - Production Only
if (process.env.NODE_ENV === 'production') {
  const script = document.createElement('script');
  script.defer = true;
  script.setAttribute('data-domain', 'wsb.bitville-numbers.com');
  script.src = 'https://plausible.omillionaire.com/js/plausible.js';
  document.head.appendChild(script);
}

// Error handling wrapper for Huawei compatibility
async function initializeApp() {
  try {
    // Log browser compatibility for debugging
    logBrowserCompatibility()
    
    // Special handling for Huawei devices
    if (isHuaweiDevice()) {
      console.log('🔍 Huawei device detected - applying compatibility measures')
    }
    
    if (isOldAndroid()) {
      console.log('⚠️ Old Android version detected - using fallback features')
    }
    
    const app = createApp(App)
    const pinia = createPinia()

    // Setup i18n
    const i18n = await setupI18n()

    app.use(pinia)
    app.use(i18n)
    app.use(router)

    // Initialize stores
    const authStore = useAuthStore()
    const themeStore = useThemeStore()

    // Initialize auth store from session storage
    authStore.initializeFromSession()

    // Setup cache invalidation on tab close
    setupCacheInvalidation()

    // Resolve the requested mode before loading the account theme so the correct
    // per-mode palette is applied on first paint.
    try {
      let mode = readModeQueryFromWindow()
      if (!mode && iframeCommunication.isRunningInIframe()) {
        mode = 'dark'
      }
      themeStore.initializeFromUrl(mode || undefined)
    } catch (urlError) {
      console.warn('URL parameter parsing failed, using default theme:', urlError)
      themeStore.initializeFromUrl(undefined)
    }

    await initializeTheme().catch((error) => {
      console.error('Failed to initialize theme:', error)
    })

    syncSystemColorSchemeListener()

    app.mount('#app')
    
    console.log('App initialized successfully')
  } catch (error) {
    console.error('App initialization failed:', error)
    
    // Fallback: Show basic error message
    const appElement = document.getElementById('app')
    if (appElement) {
      appElement.innerHTML = `
        <div style="padding: 20px; text-align: center; font-family: Arial, sans-serif;">
          <h2>${fallbackText('applicationError')}</h2>
          <p>${fallbackText('incompatibleDevice')}</p>
          <p>${fallbackText('tryDifferentBrowser')}</p>
          <p>${fallbackText('applicationError')}: ${(error as Error).message || fallbackText('unknownError')}</p>
        </div>
      `
    }
  }
}

// Check for required features before initializing
function checkBrowserCompatibility() {
  const requiredFeatures = [
    'Promise',
    'fetch',
    'URLSearchParams',
    'localStorage',
    'sessionStorage'
  ]
  
  const missingFeatures = requiredFeatures.filter(feature => {
    try {
      return typeof (window as any)[feature] === 'undefined'
    } catch {
      return true
    }
  })
  
  if (missingFeatures.length > 0) {
    console.error('Missing required browser features:', missingFeatures)
    const appElement = document.getElementById('app')
    if (appElement) {
      appElement.innerHTML = `
        <div style="padding: 20px; text-align: center; font-family: Arial, sans-serif;">
          <h2>${fallbackText('browserCompatibilityError')}</h2>
          <p>${fallbackText('missingFeatures')}: ${missingFeatures.join(', ')}</p>
          <p>${fallbackText('tryDifferentBrowser')}</p>
        </div>
      `
    }
    return false
  }
  
  return true
}

// Initialize app with compatibility check
if (checkBrowserCompatibility()) {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp)
  } else {
    initializeApp()
  }
}
